import { API_URL } from "./constants";
import { authenticatedFetch } from "./auth";

/**
 * @typedef {Object} LessonDTO
 * @property {number} ID
 * @property {number} StartDateUTC
 * @property {number} EndDateUTC
 * @property {string} StartTime
 * @property {string} EndTime
 * @property {number} ToBook
 * @property {string} Name
 * @property {number} MaxPartecipants
 * @property {number} TotalBookings
 */

/**
 * @typedef {Object} BookingResponseDTO
 * @property {number} ID
 * @property {number} Start      - Unix timestamp for start
 * @property {number} End        - Unix timestamp for end
 * @property {number} LessonID
 * @property {number} Reserved   - 1 = active reservation
 * @property {LessonDTO} Lesson  - the nested lesson details
 */

/**
 * Fetch available slots for a given type and date.
 * @param {string} type - e.g. "SAUNA", "GYM", "RESTAURANT"
 * @param {string} date - date in YYYY-MM-DD format
 * @returns {Promise<LessonDTO[]>} Resolves with an array of LessonDTO objects
 */
export async function fetchAvailableSlots(type, date) {
  const res = await authenticatedFetch(
    `${API_URL}/availableSlots/${date}/${type}`,
    {
      method: "GET",
    }
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  /** @type {LessonDTO[]} */
  return res.json();
}

/**
 * Book a specific slot.
 * @param {string} lessonID
 * @param {string} date - date in YYYY-MM-DD format
 * @param {any} [notes] - optional field for restaurant bookings
 * @returns {Promise<number>} Resolves with the booking ID
 */
export async function bookSlot(lessonID, date, notes = null) {
  const body = { lessonID, date };
  if (notes !== null) body.notes = notes;
  const res = await authenticatedFetch(
    `${API_URL}/bookingSlot`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    }
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  const { ID } = await res.json();
  return ID;
}

/**
 * Fetch all booked slots filtered by type.
 * @param {string} type - e.g. "SAUNA", "GYM", "RESTAURANT"
 * @returns {Promise<BookingResponseDTO[]>} Resolves with an array of BookingResponseDTO objects
 */
export async function fetchBookedSlots(type) {
  const res = await authenticatedFetch(
    `${API_URL}/bookedSlot/${type}`,
    {
      method: "GET",
    }
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  /** @type {BookingResponseDTO[]} */
  const bookings = await res.json();

  bookings.sort((a, b) => {
    if (a.Start !== b.Start) {
      return a.Start - b.Start;
    }
    if (a.Lesson?.StartTime && b.Lesson?.StartTime) {
      return a.Lesson.StartTime.localeCompare(b.Lesson.StartTime);
    }
    return 0;
  });

  return bookings;
}

/**
 * Delete a booking by lessonID and date.
 * @param {string} lessonID
 * @param {string} date - date in YYYY-MM-DD format
 * @returns {Promise<boolean>} Resolves to true if deletion succeeded
 */
export async function deleteBooking(lessonID, date) {
  const res = await authenticatedFetch(
    `${API_URL}/deleteBooking/${lessonID}/${date}`,
    {
      method: "DELETE",
    }
  );
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return true;
}
